import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { referralService } from '../referralService';
import { ILinkReferralAccount } from '../referralTypes';

const REFERRAL_API_ERROR_MESSAGES: Record<string, string> = {
  'Link is required': 'You need to enter a referral link first',
  'Invalid referral link': 'Referral link is invalid',
};

const DEFAULT_NETWORK_ERROR_MESSAGE = 'An error occurred while linking the referral account.';
const DEFAULT_GENERIC_ERROR_MESSAGE = 'An unexpected error occurred.';

export const useLinkReferralAccount = () => {
  const mutation = useMutation<ILinkReferralAccount, Error, string>({
    mutationKey: ['linkReferralAccount'],
    mutationFn: (referralLink: string) => referralService.linkReferralAccount(referralLink),
  });

  const { mutate, isPending, error, isSuccess } = mutation;

  const getErrorMessage = (err: Error | AxiosError | null): string | undefined => {
    if (!err) {
      return undefined;
    }

    if ('isAxiosError' in err) {
      const apiErrorMessage = (err.response?.data as any)?.errors?.[0]?.message;

      if (apiErrorMessage) {
        return REFERRAL_API_ERROR_MESSAGES[apiErrorMessage] || apiErrorMessage;
      }

      return (err.response?.data as any)?.message || DEFAULT_NETWORK_ERROR_MESSAGE;
    }

    return err.message || DEFAULT_GENERIC_ERROR_MESSAGE;
  };

  const errorMessage = getErrorMessage(error);

  return { mutate, isPending, errorMessage, isSuccess };
};
